import React, { FC, memo, useState } from "react";

// UI
import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import { Icon } from "@UI/Components/Icon/Icon";
import { VStack, HStack } from "@UI/Components/Stack/Stack";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { BrandPlus } from "@UI/Components/Icons/BrandPlus";
import { Check } from "@UI/Components/Icons/Check";
import { ShieldCheck } from "@UI/Components/Icons/ShieldCheck";
import { Coins } from "@UI/Components/Icons/Coins";
import { StarRegular } from "@UI/Components/Icons/StarRegular";
import { Ban } from "@UI/Components/Icons/Ban";
import { Or } from "@UI/Components/Or/Or";
import {
  Wizard,
  WizardContent,
  WizardHeader,
  WizardNav,
} from "@Shared/Components/Layouts/Wizard/Wizard";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { Container } from "@UI/Components/Container/Container";
import { FAQSection } from "@modules/CustomerSupport/Components/FAQSection/FAQSection";
import useMediaQuery from "@UI/hooks/useMediaQuery";

// i18n
import messages from "./messages";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { FormattedNumber } from "@I18n/Components/FormattedNumber/FormattedNumber";

// hooks
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { trackEvent } from "@Libs/GrowthBook/services/growthbook";
import { Checkout } from "@modules/Purchasing/Flows/Checkout/Checkout";
import { useServiceFeesFeature } from "@modules/ServiceFees/hooks/useServiceFeesFeature";
import { useSubscriptionPlans } from "@modules/Subscriptions/hooks/useSubscriptionPlans";
import { useServiceRequestCategory } from "@modules/ServiceRequest/hooks/useServiceRequestCategory";

// types
import { IServiceOffer } from "@modules/ServiceAssignment/services/serviceOffers";
import { IServiceProvider } from "@modules/ServiceProviders/services/serviceProviders";

// local
import { computePricingBreakdown } from "./computePricingBreakdown";
import { selectDescriptionMessage } from "./selectDescriptionMessage";
import { FormulaFeeBreakdown } from "./FormulaFeeBreakdown";

// seaGreen.500 — primary green used for Standard selected state
const SEA_GREEN = "#02BF6D";
const PINK_500 = "#FF478F";
const ORANGE_500 = "#FF9800";

const FA_ICON_STYLE = {
  color: PINK_500,
  fontSize: "14px",
  flexShrink: 0,
} as const;
const CHECK_ICON_STYLE = { color: SEA_GREEN, fontSize: "12px" } as const;

type TSelectedFormula = "subscription" | "service_fee" | null;

interface IProps {
  serviceOffer: IServiceOffer;
  serviceProvider: IServiceProvider;
  serviceOfferId: string;
  onBack: () => void;
}

interface IFormulaCardProps {
  isSelected: boolean;
  onClick: () => void;
  children: React.ReactNode;
  variant: "standard" | "subscription";
}

const FormulaCard: FC<IFormulaCardProps> = memo(function FormulaCard({
  isSelected,
  onClick,
  children,
  variant,
}) {
  const borderBackground = isSelected
    ? variant === "subscription"
      ? `linear-gradient(135deg, ${PINK_500} 0%, ${ORANGE_500} 100%)`
      : SEA_GREEN
    : "#DEDEDE";

  return (
    <Box
      as="button"
      w="full"
      textAlign="left"
      p="2px"
      borderRadius="10px"
      style={{ background: borderBackground }}
      onClick={onClick}
      _focusVisible={{ outline: "2px solid", outlineColor: "blue.400" }}
    >
      <Box
        bg="white"
        borderRadius="8px"
        p="4"
        display="flex"
        flexDirection="column"
        gap="2"
        w="full"
        position="relative"
        boxShadow={isSelected ? "none" : "0px 0px 3px #DEDEDE"}
        sx={{ "& p": { mb: 0 } }}
      >
        {children}
      </Box>
    </Box>
  );
});

interface IFormulaCheckboxProps {
  isSelected: boolean;
  variant: "standard" | "subscription";
}

const FormulaCheckbox: FC<IFormulaCheckboxProps> = memo(
  function FormulaCheckbox({ isSelected, variant }) {
    const checkedBg =
      variant === "subscription"
        ? `linear-gradient(135deg, ${PINK_500} 0%, ${ORANGE_500} 100%)`
        : SEA_GREEN;

    return (
      <Box
        position="absolute"
        top="17px"
        right="16px"
        w="20px"
        h="20px"
        borderRadius="5px"
        border={isSelected ? "none" : "1px solid"}
        borderColor="gray.300"
        style={isSelected ? { background: checkedBg } : undefined}
        bg={isSelected ? undefined : "white"}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
      >
        {isSelected && <Icon as={Check} color="white" boxSize="2.5" />}
      </Box>
    );
  }
);

export const ChooseFormulaPageActivationRate: FC<IProps> = ({
  serviceOffer,
  serviceProvider,
  serviceOfferId,
  onBack,
}) => {
  const [selectedFormula, setSelectedFormula] =
    useState<TSelectedFormula>(null);
  const navigate = useNavigate();
  const { isMobile } = useMediaQuery();
  const { currentUser } = useCurrentUser();

  const { serviceFeesFeature, isLoading: isServiceFeesLoading } =
    useServiceFeesFeature();
  const { subscriptionPlans, isLoading: isSubscriptionPlansLoading } =
    useSubscriptionPlans();
  const { categoryId: taskCategoryId, isLoading: isServiceRequestLoading } =
    useServiceRequestCategory(serviceOffer.serviceRequestId);

  if (isServiceFeesLoading || isSubscriptionPlansLoading || isServiceRequestLoading) {
    return <LoadingScreen />;
  }

  const pricing = computePricingBreakdown(serviceOffer, serviceFeesFeature, subscriptionPlans);
  const {
    isVisitOffer,
    unitNode,
    serviceFeePercentage,
    serviceFeeAmountCents,
    totalExpensesCents,
    expensesFeesCents,
    standardTotalCents,
    subscriptionTotalCents,
    quarterlyPlan,
  } = pricing;

  const descriptionMessage = selectDescriptionMessage({
    isVisitOffer,
    quarterlyPlan,
    serviceFeeAmountCents,
    taskCategoryId,
  });

  const serviceProviderName = serviceProvider?.user?.firstName;

  const baseEventProps = {
    contractId: Number(serviceOfferId),
    serviceRequestId: serviceOffer?.serviceRequestId,
    clientId: currentUser?.id,
    clientEmail: currentUser?.email,
    isSubscribed: currentUser?.subscribed ?? false,
  };

  const handleFormulaCardSelect = (formula: "subscription" | "service_fee") => {
    setSelectedFormula(formula);
    // activation_rate experiment events — safe to remove once experiment is concluded
    trackEvent("formula_card_selected", { ...baseEventProps, formula });
  };

  const handleContinue = () => {
    if (selectedFormula) {
      // activation_rate experiment events — safe to remove once experiment is concluded
      trackEvent("choose_formula_next_clicked", { ...baseEventProps, formula: selectedFormula });
    }
    if (selectedFormula === "subscription") {
      navigate(
        ["SERVICE_OFFERS", "SERVICE_OFFER", "ACCEPTANCE", "SUBSCRIBE", Checkout.root],
        { params: { serviceOfferId } }
      );
    } else if (selectedFormula === "service_fee") {
      navigate(
        ["SERVICE_OFFERS", "SERVICE_OFFER", "ACCEPTANCE", "SERVICE_FEE"],
        { params: { serviceOfferId } }
      );
    }
  };

  return (
    <>
      <Wizard>
        <WizardNav>
          <BackLink onClick={onBack} />
        </WizardNav>
        <WizardHeader marginBottom={{ base: 3, md: 4 }}>
          <Text
            as="h3"
            fontSize={{ base: "2xl", lg: "4xl" }}
            fontWeight="normal"
            fontFamily="'Jornada Sans Book', sans-serif"
          >
            <FormattedMessage {...messages.title} />
          </Text>
        </WizardHeader>
        <WizardContent>
          <Box
            textAlign={{ base: "left", lg: "center" }}
            w={{ base: "full", lg: "50%" }}
            mx={{ lg: "auto" }}
          >
            <Text
              fontSize="sm"
              color="gray.500"
              style={{ textWrap: "balance" } as React.CSSProperties}
            >
              <FormattedMessage
                {...descriptionMessage}
                values={{ serviceProviderName }}
              />
            </Text>
          </Box>

          <VStack mt={{ base: 0, lg: 4 }} spacing={{ base: "4", lg: "8" }} alignItems="stretch">
            {/* Cards — row on desktop (lg+), stacked on mobile */}
            <Flex
              flexDir={{ base: "column", lg: "row" }}
              justifyContent={{ lg: "center" }}
              alignItems={{ base: "stretch", lg: "flex-start" }}
              gap={{ base: "4", lg: "0" }}
            >
              {/* Subscription card */}
              <Box w={{ base: "full", lg: "343px" }} flexShrink={0}>
                <FormulaCard
                  variant="subscription"
                  isSelected={selectedFormula === "subscription"}
                  onClick={() => handleFormulaCardSelect("subscription")}
                >
                  <FormulaCheckbox
                    variant="subscription"
                    isSelected={selectedFormula === "subscription"}
                  />

                  <Heading as="h3" fontSize="xl" textAlign="center" h="28px">
                    <FormattedMessage
                      {...messages.ringTwicePlusTitle}
                      values={{
                        plus: () => (
                          <Icon
                            as={BrandPlus}
                            boxSize="4"
                            marginLeft="1"
                            display="inline"
                          />
                        ),
                      }}
                    />
                  </Heading>

                  <Heading
                    as="p"
                    fontSize="3xl"
                    lineHeight="1"
                    mb="1"
                    textAlign="center"
                    bgGradient="linear(96.51deg, pink.500, orange.500)"
                    bgClip="text"
                  >
                    {isVisitOffer ? (
                      <FormattedMessage
                        {...messages.noFeeOnQuote}
                        values={{
                          bold: ((chunks: React.ReactNode) => (
                            <Text as="span" fontWeight="bold">
                              {chunks}
                            </Text>
                          )) as unknown as () => React.ReactNode,
                        }}
                      />
                    ) : unitNode ? (
                      <>
                        <FormattedNumber
                          value={serviceOffer.rateCents / 100}
                          style="currency"
                          currency={serviceOffer.rateCurrency}
                          minimumFractionDigits={0}
                        />
                        {unitNode}
                        {totalExpensesCents > 0 && (
                          <>
                            {" + "}
                            <FormattedNumber
                              value={totalExpensesCents / 100}
                              style="currency"
                              currency={serviceOffer.rateCurrency}
                              minimumFractionDigits={0}
                            />
                          </>
                        )}
                      </>
                    ) : (
                      <FormattedNumber
                        value={subscriptionTotalCents / 100}
                        style="currency"
                        currency={serviceOffer.rateCurrency}
                        minimumFractionDigits={0}
                      />
                    )}
                  </Heading>

                  <Text fontSize="sm" color="gray.500" mb="3" textAlign="center">
                    <FormattedMessage {...messages.paidAfterServiceDone} />{" "}
                    <Check style={CHECK_ICON_STYLE} />
                  </Text>

                  <FormulaFeeBreakdown
                    variant="subscription"
                    serviceOffer={serviceOffer}
                    serviceProviderName={serviceProviderName}
                    isVisitOffer={isVisitOffer}
                    unitNode={unitNode}
                    serviceFeePercentage={serviceFeePercentage}
                    serviceFeeAmountCents={serviceFeeAmountCents}
                    totalExpensesCents={totalExpensesCents}
                    expensesFeesCents={expensesFeesCents}
                  />

                  {/* Feature bullets */}
                  <Box
                    borderTop="1px solid"
                    borderColor="gray.200"
                    pt="2"
                    mb="3"
                  >
                    <VStack spacing="2" alignItems="flex-start">
                      <HStack spacing="2">
                        <ShieldCheck style={FA_ICON_STYLE} />
                        <Text fontSize="sm" color="gray.500">
                          <FormattedMessage {...messages.jobInsurance} />
                        </Text>
                      </HStack>
                      <HStack spacing="2">
                        <Coins style={FA_ICON_STYLE} />
                        <Text fontSize="sm" color="gray.500">
                          <FormattedMessage
                            {...messages.noServiceFee}
                            values={{
                              bold: ((chunks: React.ReactNode) => (
                                <Text as="span" fontWeight="bold" color="pink.500">
                                  {chunks}
                                </Text>
                              )) as unknown as () => React.ReactNode,
                            }}
                          />
                        </Text>
                      </HStack>
                      <HStack spacing="2">
                        <StarRegular style={FA_ICON_STYLE} />
                        <Text fontSize="sm" color="gray.500">
                          <FormattedMessage {...messages.satisfiedOrRedone} />
                        </Text>
                      </HStack>
                      <HStack spacing="2">
                        <Ban style={FA_ICON_STYLE} />
                        <Text fontSize="sm" color="gray.500">
                          <FormattedMessage {...messages.cancellationFree} />
                        </Text>
                      </HStack>
                    </VStack>
                  </Box>

                  {/* Subscription badge */}
                  {quarterlyPlan && (
                    <Box
                      bg="#FFEACC"
                      borderRadius="10px"
                      py="8px"
                      px="12px"
                      textAlign="center"
                    >
                      <Text fontSize="sm" color="gray.800">
                        <FormattedMessage
                          {...messages.subscriptionBadge}
                          values={{
                            price: (
                              <FormattedNumber
                                value={quarterlyPlan.priceCents / 100}
                                style="currency"
                                currency={quarterlyPlan.priceCurrency}
                                minimumFractionDigits={2}
                              />
                            ),
                          }}
                        />
                      </Text>
                    </Box>
                  )}
                </FormulaCard>
              </Box>

              <Box
                display={{ base: "none", lg: "flex" }}
                w="100px"
                alignSelf="stretch"
                alignItems="center"
                justifyContent="center"
                flexShrink={0}
              >
                <Or orientation="vertical" />
              </Box>

              {/* Standard card */}
              <Box w={{ base: "full", lg: "343px" }} flexShrink={0}>
                <FormulaCard
                  variant="standard"
                  isSelected={selectedFormula === "service_fee"}
                  onClick={() => handleFormulaCardSelect("service_fee")}
                >
                  <FormulaCheckbox
                    variant="standard"
                    isSelected={selectedFormula === "service_fee"}
                  />

                  <Text
                    fontWeight="medium"
                    color="gray.700"
                    mb="2"
                    textAlign="center"
                    h="28px"
                  >
                    <FormattedMessage {...messages.standardTitle} />
                  </Text>

                  <Heading
                    as="p"
                    fontSize="3xl"
                    lineHeight="1"
                    mb="1"
                    color="gray.800"
                    textAlign="center"
                  >
                    {isVisitOffer ? (
                      <FormattedMessage {...messages.quotePrice} />
                    ) : unitNode ? (
                      <>
                        <FormattedNumber
                          value={(serviceOffer.rateCents + serviceFeeAmountCents) / 100}
                          style="currency"
                          currency={serviceOffer.rateCurrency}
                          minimumFractionDigits={0}
                        />
                        {unitNode}
                        {totalExpensesCents + expensesFeesCents > 0 && (
                          <>
                            {" + "}
                            <FormattedNumber
                              value={(totalExpensesCents + expensesFeesCents) / 100}
                              style="currency"
                              currency={serviceOffer.rateCurrency}
                              minimumFractionDigits={0}
                            />
                          </>
                        )}
                      </>
                    ) : (
                      <FormattedNumber
                        value={standardTotalCents / 100}
                        style="currency"
                        currency={serviceOffer.rateCurrency}
                        minimumFractionDigits={0}
                      />
                    )}
                  </Heading>

                  <Text fontSize="sm" color="gray.500" mb="3" textAlign="center">
                    <FormattedMessage {...messages.paidAfterServiceDone} />{" "}
                    <Check style={CHECK_ICON_STYLE} />
                  </Text>

                  <FormulaFeeBreakdown
                    variant="standard"
                    serviceOffer={serviceOffer}
                    serviceProviderName={serviceProviderName}
                    isVisitOffer={isVisitOffer}
                    unitNode={unitNode}
                    serviceFeePercentage={serviceFeePercentage}
                    serviceFeeAmountCents={serviceFeeAmountCents}
                    totalExpensesCents={totalExpensesCents}
                    expensesFeesCents={expensesFeesCents}
                  />

                  <Box borderTop="1px solid" borderColor="gray.200" pt="2">
                    <HStack spacing="2">
                      <ShieldCheck style={FA_ICON_STYLE} />
                      <Text fontSize="sm" color="gray.500">
                        <FormattedMessage {...messages.jobInsurance} />
                      </Text>
                    </HStack>
                  </Box>
                </FormulaCard>
              </Box>
            </Flex>

            <Button
              colorScheme="seaGreen"
              size="lg"
              w="full"
              isDisabled={!selectedFormula}
              onClick={handleContinue}
              data-testid="choose-formula-continue"
            >
              <FormattedMessage {...messages.continueButton} />
            </Button>
            {isMobile && (
              <Box marginTop="10">
                <FAQSection />
              </Box>
            )}
          </VStack>
        </WizardContent>
      </Wizard>
      {!isMobile && (
        <Container
          marginY={[10, 10, 20]}
          maxW={["container.lg", "container.md", "container.lg"]}
        >
          <FAQSection />
        </Container>
      )}
    </>
  );
};
